import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Alert,
  Box,
  Button,
  Divider,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Legend,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';
import { IconLockClosedFilled } from '@zillow/constellation-icons';
import { logError } from '@zg-rentals/log-error';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { useUpdateLandlordPaymentMethodWebApiHandler_updateLandlordPaymentMethod } from '@zg-rentals/ts-rent-payment-client-react-query';

import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import PlaidButton from '@common/components/PlaidButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import userPersonas from '@common/constants/userPersonas';
import { DEFAULT_ERROR, INVALID_ACCOUNT_INFO } from '@rental-manager/constants/NotificationConstants';
import MaskedInput from '@common/components/ConstellationMaskedInput';
import errorMessages from '@common/constants/errorMessages';
import { validateCheckingAccount, validateRoutingNumber } from '@common/paymentsUtils/inputValidator';
import { validateBankInfo } from './validation';
import type { IConfigModel } from '@rental-manager/models/Types';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import { closeNotification } from '@common/actions/NotificationActions';
import { saveAccountInfo } from '@rental-manager/actions/OnboardingActions';

declare const window: {
  Stripe: any;
};

interface Props {
  config: IConfigModel;
  fromDashboard: boolean;
  onBoarded: boolean;
  accountInfo: {
    editMode: boolean;
    paymentType: string;
    routingNumber: string;
    checkingAccountNumber: string;
  };
  onClose: () => void;
  onCancel: () => void;
  onUpdatePaymentMethodSuccess: () => void;
  onBack: ({ paymentType, routingNumber, checkingAccountNumber }) => void;
  trackValidationError: ({ plaidErrorsLastEncountered, plaidMetadata }) => void;
  showModalNotification: (name: string, extraArgs?: Record<string, unknown>) => Record<string, any>;
  getPlaidLinkToken: () => any;
  selectedTaxVerificationType: string;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClickForStorybook?: () => void;
  propertyId: string;
}

interface TokenResponse {
  token: string;
  error: string;
}

type PaymentMethodToken = {
  plaidToken?: Record<string, any>;
  stripeExternalAccountToken?: string;
};

type UpdatePaymentPackage = {
  accountInfo?: {
    paymentType: string;
    plaidBankName?: string;
    routingNumber?: string;
    checkingAccountNumber?: string;
  };
  token: PaymentMethodToken;
};

const AccountInfoPage: React.FC<Props> = ({
  config,
  fromDashboard = false,
  onBoarded = false,
  accountInfo = {
    paymentType: 'plaid',
    routingNumber: '',
    checkingAccountNumber: '',
    editMode: false,
  },
  onClose,
  onCancel,
  onUpdatePaymentMethodSuccess,
  onBack,
  trackValidationError,
  showModalNotification,
  propertyId,
  getPlaidLinkToken,
  selectedTaxVerificationType,
  currentProperty,
  user,
  onClickForStorybook,
}) => {
  const dispatch = useDispatch();
  const userZuid = useSelector((state: any) => state.user?.userToken);

  const [stripe, setStripe] = useState<any>(null);
  const [paymentType, setPaymentType] = useState<string>(accountInfo.paymentType);
  const [routingNumber, setRoutingNumber] = useState({
    value: accountInfo.routingNumber,
    error: '',
  });
  const [checkingAccountNumber, setCheckingAccountNumber] = useState({
    value: accountInfo.checkingAccountNumber,
    error: '',
  });
  const [confirmAccountNumber, setConfirmAccountNumber] = useState({
    value: '',
    error: '',
  });
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { mutateAsync: updateLandlordPaymentMethod } =
    useUpdateLandlordPaymentMethodWebApiHandler_updateLandlordPaymentMethod();

  // Ref for package data
  const pkgRef = useRef<UpdatePaymentPackage>({
    accountInfo: {
      paymentType: '',
      plaidBankName: '',
      routingNumber: '',
      checkingAccountNumber: '',
    },
    token: {
      plaidToken: {},
    },
  });

  // Effect hook to replace componentDidMount
  useEffect(() => {
    // https://github.com/stripe/react-stripe-elements#loading-stripejs-asynchronously
    // note: useEffect doesn't run on the server
    if (window.Stripe) {
      setStripe(window.Stripe(config.stripePublicKey));
    } else {
      const el = document.querySelector('#stripe-js');
      el?.addEventListener('load', () => {
        setStripe(window.Stripe(config.stripePublicKey));
      });
    }
  }, [config.stripePublicKey]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.dataset.statekey;
    const value = e.target.value.replace(/[^\d]/g, '');

    const setterMap = {
      routingNumber: setRoutingNumber,
      checkingAccountNumber: setCheckingAccountNumber,
      confirmAccountNumber: setConfirmAccountNumber,
    };

    if (key && setterMap[key]) {
      setterMap[key]({ value, error: '' });
    }
  };

  /* Blur flags invalid content only; emptiness stays a submit-time concern so tabbing
     through the form doesn't scatter errors. The ABA checksum is only knowable once all
     9 digits are present, which is why this cannot be gated at input time. */
  const onRoutingNumberBlur = (): void => {
    const value = routingNumber.value;
    if (!value) {
      return;
    }
    if (value.length !== 9) {
      setRoutingNumber({ value, error: errorMessages.ROUTING_NUMBER_INVALID });
    } else if (!validateRoutingNumber(value)) {
      setRoutingNumber({ value, error: errorMessages.ROUTING_NUMBER_DEFAULT_ERROR });
    }
  };

  const onCheckingAccountBlur = (): void => {
    const value = checkingAccountNumber.value;
    if (value && validateCheckingAccount(value) !== true) {
      setCheckingAccountNumber({ value, error: errorMessages.CHECKING_ACCOUNT_NUMBER_INVALID });
    }
  };

  const onConfirmAccountBlur = (): void => {
    const value = confirmAccountNumber.value;
    if (value && checkingAccountNumber.value && value !== checkingAccountNumber.value) {
      setConfirmAccountNumber({ value, error: errorMessages.CHECKING_ACCOUNT_NUMBER_CONFIRMATION_NOT_MATCH });
    }
  };

  const handleShowLoadingScreen = (): void => {
    setShowLoadingScreen(true);
  };

  const handleHideLoadingScreen = (): void => {
    setShowLoadingScreen(false);
  };

  const toggleConfirmationModal = (): void => {
    setShowConfirmationModal(!showConfirmationModal);
  };

  const changePaymentType = (e: React.MouseEvent) => {
    e.preventDefault();
    const trackingData = { currentProperty, user };
    IreneOnboardingCSEvents.emitLinkToAccountManuallyFromAccountInfoPageEvent(trackingData);
    IreneOnboardingCSEvents.emitContinueManuallyFromAccountInfoPageEvent({ ...trackingData });
    setPaymentType('bankAccount');
  };

  const trackOnClickPlaidButton = (): void => {
    IreneOnboardingCSEvents.emitLinkThroughPlaidFromAccountInfoPageEvent({
      currentProperty,
      user,
    });
  };

  const gotoPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'account_info_modal',
    });
    onBack({
      paymentType,
      routingNumber: routingNumber.value,
      checkingAccountNumber: checkingAccountNumber.value,
    });
  };

  const handleClose = (): void => {
    if (!fromDashboard) {
      IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
        currentProperty,
        user,
        triggerObject: 'account_info_modal',
      });
    }

    onClose();
  };

  const handleCancel = (): void => {
    onCancel();
  };

  const onPlaidSuccess = async (token: any) => {
    handleShowLoadingScreen();
    pkgRef.current = {
      accountInfo: {
        paymentType: 'plaid',
        plaidBankName: token.bankName,
        routingNumber: '',
        checkingAccountNumber: '',
      },
      token: {
        plaidToken: token,
      },
    };

    if (onBoarded) {
      toggleConfirmationModal();
      handleHideLoadingScreen();
      return null;
    }

    await updateDepositMethodAndNavigate();
  };

  const onPlaidExit = (err: any, metadata: any): void => {
    trackValidationError({
      plaidErrorsLastEncountered: err,
      plaidMetadata: metadata,
    });
  };

  const trackOnSubmit = (): void => {
    IreneOnboardingCSEvents.emitContinueFromAccountInfoPageEvent({
      currentProperty,
      user,
    });
  };

  const createStripeBankAccountToken = (): Promise<TokenResponse> => {
    return new Promise((resolve) => {
      stripe
        .createToken('bank_account', {
          country: 'us',
          currency: 'usd',
          routing_number: routingNumber.value,
          account_number: checkingAccountNumber.value,
          account_holder_type: 'individual',
        })
        .then(({ error, token }) => {
          if (error) {
            resolve({ error: error.type, token: '' });
          } else {
            resolve({ error: '', token: token.id });
          }
        });
    });
  };

  const createBankAccountToken = (): Promise<TokenResponse> => {
    trackOnSubmit();
    if (stripe) {
      return createStripeBankAccountToken();
    } else {
      return new Promise((resolve) => resolve({ error: 'No payment provider set', token: '' }));
    }
  };

  const handleSubmitBankAccount = (): Promise<any> | boolean => {
    // Create mutable copies for validation
    const stateForValidation = {
      routingNumber: { value: routingNumber.value, error: routingNumber.error },
      checkingAccountNumber: { value: checkingAccountNumber.value, error: checkingAccountNumber.error },
      confirmAccountNumber: { value: confirmAccountNumber.value, error: confirmAccountNumber.error },
    };

    const validInputs = validateBankInfo({
      state: stateForValidation,
      setState: (newState: any) => {
        setRoutingNumber(newState.routingNumber);
        setCheckingAccountNumber(newState.checkingAccountNumber);
        setConfirmAccountNumber(newState.confirmAccountNumber);
      },
    });
    if (!validInputs) {
      return false;
    }
    return Promise.resolve()
      .then(() => handleShowLoadingScreen())
      .then(createBankAccountToken)
      .then(({ error, token }) => {
        if (error) {
          logError({ error });
          showModalNotification(INVALID_ACCOUNT_INFO);
          return { error };
        }

        pkgRef.current = {
          accountInfo: {
            paymentType,
            routingNumber: routingNumber.value.trim(),
            checkingAccountNumber: checkingAccountNumber.value.trim(),
          },
          token: {
            stripeExternalAccountToken: token,
          },
        };

        if (onBoarded) {
          toggleConfirmationModal();
          handleHideLoadingScreen();
          return null;
        }
        updateDepositMethodAndNavigate();
      });
  };

  const updateDepositMethodAndNavigate = async () => {
    handleShowLoadingScreen();
    try {
      await dispatch(closeNotification());
      await dispatch(saveAccountInfo({ propertyId, accountInfo: pkgRef.current.accountInfo }));
      await updateLandlordPaymentMethod({
        postData: {
          propertyId,
          ...(pkgRef.current.token.stripeExternalAccountToken && {
            stripeExternalAccountToken: pkgRef.current.token.stripeExternalAccountToken,
          }),
          ...(pkgRef.current.token.plaidToken?.publicToken && {
            plaidToken: {
              publicToken: pkgRef.current.token.plaidToken.publicToken,
              accountId: pkgRef.current.token.plaidToken.accountId || '',
            },
          }),
        },
      });
      onUpdatePaymentMethodSuccess();
    } catch (err) {
      logError({ error: err });
      showModalNotification(DEFAULT_ERROR, { subject: 'Deposit account' });
    } finally {
      handleHideLoadingScreen();
    }
  };

  const renderPlaidComponent = (): JSX.Element => (
    <Box
      css={{
        display: 'grid',
        gridTemplateRows: 'auto',
        gap: 'default',
      }}
    >
      <Button
        emphasis="outlined"
        css={{ justifySelf: 'start' }}
        onClick={changePaymentType}
        disabled={showLoadingScreen}
      >
        Manually enter account information
      </Button>
    </Box>
  );

  const renderBankAccountComponent = (): JSX.Element => (
    <>
      <FieldSet legend={<Legend>Checking account</Legend>}>
        <Box css={{ display: 'grid', gap: 'default' }}>
          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(routingNumber.error)}
            required
            control={
              <MaskedInput
                mask="999999999"
                required
                error={routingNumber.error}
                onChange={onInputChange}
                onBlur={onRoutingNumberBlur}
                value={routingNumber.value}
                className="fs-exclude"
                data-statekey="routingNumber"
                inputMode="numeric"
              />
            }
            description={
              routingNumber.error ? (
                <FormHelp>{routingNumber.error}</FormHelp>
              ) : (
                <FormHelp>9 digits, found on the bottom left of your check</FormHelp>
              )
            }
            label={<Label>Routing number</Label>}
          />

          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(checkingAccountNumber.error)}
            required
            control={
              <Input
                fluid
                size="md"
                onChange={onInputChange}
                onBlur={onCheckingAccountBlur}
                value={checkingAccountNumber.value}
                className="fs-exclude"
                data-statekey="checkingAccountNumber"
                inputMode="numeric"
              />
            }
            description={
              checkingAccountNumber.error ? (
                <FormHelp>{checkingAccountNumber.error}</FormHelp>
              ) : (
                <FormHelp>Found to the right of the routing number on your check</FormHelp>
              )
            }
            label={<Label>Account number</Label>}
          />

          <FormField
            css={{ maxWidth: '36rem' }}
            error={Boolean(confirmAccountNumber.error)}
            required
            control={
              <Input
                fluid
                size="md"
                onChange={onInputChange}
                onBlur={onConfirmAccountBlur}
                value={confirmAccountNumber.value}
                className="fs-exclude"
                data-statekey="confirmAccountNumber"
                inputMode="numeric"
              />
            }
            description={confirmAccountNumber.error && <FormHelp>{confirmAccountNumber.error}</FormHelp>}
            label={<Label>Confirm account number</Label>}
          />
        </Box>
      </FieldSet>
    </>
  );

  const getFaqComponent = (): JSX.Element => {
    let faqs = FaqConstants.ONBOARDING_V2.ACCOUNT_INFO_PAGE_FAST_PAYMENTS;

    if (fromDashboard) {
      faqs = faqs.concat(FaqConstants.DASHBOARD_V2.EDIT_ACCOUNT_INFO_PAGE);
    }
    return <FaqComponent faqs={faqs} />;
  };

  const renderMainContent = (): JSX.Element => (
    <>
      {(selectedTaxVerificationType === 'single_member_llc' || selectedTaxVerificationType === 'multi_member_llc') && (
        <Alert tone="info">
          {selectedTaxVerificationType === 'single_member_llc'
            ? 'The name of the bank account’s holder must match the owner’s name, business name, or DBA name you provided'
            : 'The name of the bank account’s holder must match the business name or DBA name you provided'}
        </Alert>
      )}

      <Heading level={3} textStyle="body-lg-bold">
        Where would you like rent to be deposited?
      </Heading>

      <Box css={{ maxWidth: '36rem' }}>
        <PlaidButton
          plaidEnv={config.plaidEnv}
          plaidLinkToken={config.plaidLinkToken}
          plaidLinkTokenExpiration={config.plaidLinkTokenExpiration}
          getPlaidLinkToken={getPlaidLinkToken}
          label="Link my bank account"
          IconComponent={<IconLockClosedFilled />}
          emphasis={paymentType === 'plaid' ? 'filled' : 'outlined'}
          onSuccess={onPlaidSuccess}
          onExit={onPlaidExit}
          onClickTracking={trackOnClickPlaidButton}
          dataTest="landlord-account-info-link-bank"
          useConstellationButton
          disabled={showLoadingScreen}
          onClickForStorybook={onClickForStorybook}
          userZuid={userZuid}
        />
      </Box>
      <Paragraph textStyle="body-sm">Fastest — you only need your bank login info</Paragraph>
      <Divider />
      {paymentType === 'plaid' ? renderPlaidComponent() : renderBankAccountComponent()}
    </>
  );

  const renderConfirmationModal = (): JSX.Element => (
    <>
      <Heading level={3} textStyle="body-lg-bold">
        Are you sure you want to update your deposit account?
      </Heading>

      <Paragraph>
        Your current deposit account will no longer be stored, and all future payments for all of your properties will
        be deposited into this new account.
      </Paragraph>
    </>
  );

  const renderFooterEnd = (): JSX.Element => {
    if (showConfirmationModal) {
      return (
        <>
          <Button emphasis="outlined" onClick={toggleConfirmationModal}>
            Cancel
          </Button>
          <DebouncedButton emphasis="filled" onClick={updateDepositMethodAndNavigate} loading={showLoadingScreen}>
            Update my account
          </DebouncedButton>
        </>
      );
    }
    return (
      <>
        {accountInfo.editMode && (
          <TextButton data-statekey="Cancel" onClick={handleCancel}>
            Cancel
          </TextButton>
        )}
        {paymentType === 'plaid' ? (
          <Button emphasis="filled" disabled loading={showLoadingScreen}>
            Continue
          </Button>
        ) : (
          <DebouncedButton
            onClick={handleSubmitBankAccount}
            dataTest="landlord-account-info-link-checking-account"
            loading={showLoadingScreen}
          >
            {accountInfo.editMode ? 'Update' : 'Continue'}
          </DebouncedButton>
        )}
      </>
    );
  };

  const renderModal = () => {
    if (showConfirmationModal) {
      return renderConfirmationModal();
    } else {
      return renderMainContent();
    }
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      aside={getFaqComponent()}
      onRequestClose={handleClose}
      header="Payment setup"
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderModal()}
        </Box>
      }
      backAction={showConfirmationModal || fromDashboard ? undefined : { onClick: gotoPreviousPage }}
      footerAriaLabel={showConfirmationModal ? 'Deposit account update confirmation' : 'Bank account setup actions'}
      footerEnd={renderFooterEnd()}
    />
  );
};

export default AccountInfoPage;
